import { App } from "@/app";
import { Auth } from "@/auth";
import { Project } from "@/project";
import { getEnvVar, metersToScene, throttle } from "@/utils";
import { FOV_RAD, LAYERS, Viewer } from "@/viewer";
import type { RasterBandInfo, RasterMetadata } from "@lumidb/lumidb";
import * as THREE from "three";
import { proxy } from "valtio";
import * as dtm from "./dtm";
import { PriorityQueue } from "./priority-queue";
import fragmentShader from "./shaders/terrain.frag?raw";
import vertexShader from "./shaders/terrain.vert?raw";
import { readImageBitmapPixels } from "./webgl-pixel-reader";

export function tileKey(z: number, x: number, y: number) {
    return `${z}-${x}-${y}`;
}

const { EARTH_RADIUS, WORLD_SIZE } = dtm;
const GRID_N = 16;
const BOX_HEIGHT_GRID_N = 4; // 5x5 height samples for a box until an exact DTM range is known
const BASE_ZOOM = 6; // root tiles anchored to the data extent at this slippy zoom
const SPLIT_FACTOR = 1.8; // split while camera distance < tile span * factor
const MAX_TILE_ZOOM = 24;
const MAX_CONCURRENT_REQUESTS = 6;
const EVAL_INTERVAL_MS = 100;
const FETCH_TIMEOUT_MS = 45_000;
const RETRY_BASE_MS = 1000; // first retry delay, doubling per attempt
const MAX_LOAD_ATTEMPTS = 4; // ~7s of backoff, then stop until "online" fires
const MAX_TILES = 1500; // sweep starts dropping above this
const TILE_KEEP_MS = 2000; // sweep spares tiles touched this recently, so a pan back finds them loaded
const MAX_DROPS_PER_SWEEP = 64; // spread the disposal cost over frames
const FRUSTUM_MARGIN = 1.1; // select a ring this much wider than the viewport, so a pan reveals loaded tiles
const MARGIN_SCALE = new THREE.Matrix4().makeScale(1 / FRUSTUM_MARGIN, 1 / FRUSTUM_MARGIN, 1);
const MIN_ELEVATION_M = -500;
const DEFAULT_Z_OFFSET_M = -5.0;
const DEFAULT_EXTENT = { minX: 0, minY: 0, maxX: 0, maxY: 0 };
const BASE_ID = "__base__";
const ORIGIN_SHIFT = WORLD_SIZE / 2;
const RETINA = window.devicePixelRatio > 1.5;
const MAPTILER_KEY = getEnvVar("VITE_MAPTILER_API_KEY");
const ESRI_TEMPLATE = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}";

export type BasemapType = "light" | "dark" | "satellite" | "esri" | "auto" | "none" | "custom";

function getMaptilerStyle(type: Exclude<BasemapType, "custom" | "none" | "esri">) {
    if (type === "light") {
        return "dataviz";
    } else if (type === "dark") {
        return "dataviz-dark";
    } else if (type === "satellite") {
        return "hybrid-v4";
    } else {
        const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
        return prefersDark ? "dataviz-dark" : "dataviz";
    }
}

export function lonLatToMercator(lon: number, lat: number): { x: number; y: number } {
    const x = ((lon * Math.PI) / 180) * EARTH_RADIUS;
    const latRad = (lat * Math.PI) / 180;
    const y = Math.log(Math.tan(Math.PI / 4 + latRad / 2)) * EARTH_RADIUS;
    return { x, y };
}

export function mercatorToLonLat(x: number, y: number): { lon: number; lat: number } {
    const lon = ((x / EARTH_RADIUS) * 180) / Math.PI;
    const lat = ((2 * Math.atan(Math.exp(y / EARTH_RADIUS)) - Math.PI / 2) * 180) / Math.PI;
    return { lon, lat };
}

// EPSG:3857 extent of slippy tile (z, x, y): x east, y north.
export function getTileExtent(z: number, x: number, y: number) {
    const span = WORLD_SIZE / 2 ** z;
    const minX = -ORIGIN_SHIFT + x * span;
    const maxY = ORIGIN_SHIFT - y * span;
    return { minX, maxX: minX + span, minY: maxY - span, maxY, span };
}

// Tile surface grid indices and uv, plus required skirt indices
function buildGrid(n: number) {
    const m = n + 1;

    const idx = (x: number, y: number) => {
        return y * m + x;
    };

    const uv: number[] = [];
    const indices: number[] = [];
    for (let y = 0; y <= n; y++) {
        for (let x = 0; x <= n; x++) {
            uv.push(x / n, y / n);
            if (x < n && y < n) {
                const a = idx(x, y);
                indices.push(a, a + m, a + 1, a + 1, a + m, a + m + 1);
            }
        }
    }

    const addSkirt = (sources: number[], twoSided = false) => {
        const bottom = uv.length / 2;

        for (const source of sources) {
            uv.push(uv[source * 2], uv[source * 2 + 1]);
        }

        for (let i = 0; i + 1 < sources.length; i++) {
            const a = sources[i];
            const b = sources[i + 1];
            const c = bottom + i;
            const d = c + 1;

            indices.push(a, b, c, b, d, c);

            if (twoSided) {
                indices.push(a, c, b, b, c, d);
            }
        }
    };

    const line = (at: (i: number) => number) => {
        return Array.from({ length: m }, (_, i) => at(i));
    };

    // Sides
    addSkirt(line((i) => idx(i, 0)));
    addSkirt(line((i) => idx(n, i)));
    addSkirt(line((i) => idx(n - i, n)));
    addSkirt(line((i) => idx(0, n - i)));

    // Middle
    addSkirt(
        line((i) => idx(n / 2, i)),
        true,
    );
    addSkirt(
        line((i) => idx(i, n / 2)),
        true,
    );

    return {
        uv: new Float32Array(uv),
        skirt: new Float32Array(uv.length / 2).fill(1, m * m),
        index: new Uint16Array(indices),
    };
}

function createTerrainMaterial(): THREE.RawShaderMaterial {
    return new THREE.RawShaderMaterial({
        glslVersion: THREE.GLSL3,
        side: THREE.FrontSide,
        vertexShader,
        fragmentShader,
        uniforms: {
            anchor: { value: new THREE.Vector2() },
            tileMask: { value: 15 },
            colorUvRect: { value: new THREE.Vector4(0, 0, 1, 1) },
            tileMeters: { value: 1 },
            heightScale: { value: 1 }, // 1 = 3D terrain, 0 = flat drape
            zOffset: { value: 0 }, // user "Terrain offset"
            baseZ: { value: 0 }, // scene Z origin
            skirtDepth: { value: 0 },
            colorTex: { value: null },
            shade: { value: 1 }, // 1 = basemap hillshade, 0 = flat overlay
            overlay: { value: 0 }, // 1 = keep texture alpha for data overlays
            opacity: { value: 1 }, // per-layer alpha
            values: { value: 0 }, // 1 = colorTex is terrarium-encoded, not a picture
            valueRange: { value: [0, 0] },
            hillshade: { value: 0 },
            sun: { value: [0, 0] }, // radians, unlike the settings
            exaggeration: { value: 1 },
            split: { value: 0 },
            contours: { value: 0 },
            contourInterval: { value: 1 },
            contourColor: { value: [0, 0, 0] }, // sRGB, converted to linear in the shader
        },
    });
}

function createDepthMaterial(): THREE.RawShaderMaterial {
    return new THREE.RawShaderMaterial({
        glslVersion: THREE.GLSL3,
        side: THREE.FrontSide,
        vertexShader,
        fragmentShader: `
            precision highp float;

            #define USE_LOGDEPTHBUF
            #define varying in
            #include <logdepthbuf_pars_fragment>

            in vec2 vUv;
            in float vSkirt;
            flat in float vTileMask;

            void main() {
                int quadrant = int(step(0.5, vUv.x)) + 2 * int(step(0.5, vUv.y));
                int coverage = 1 << quadrant;
                // A middle skirt belongs to either adjacent visible quadrant.
                if (vSkirt > 0.0) {
                    if (abs(vUv.x - 0.5) < 1e-6) coverage |= 1 << (quadrant ^ 1);
                    if (abs(vUv.y - 0.5) < 1e-6) coverage |= 1 << (quadrant ^ 2);
                }
                if ((int(vTileMask + 0.5) & coverage) == 0) discard;
                #include <logdepthbuf_fragment>
            }
        `,
        colorWrite: false,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
        uniforms: {
            anchor: { value: new THREE.Vector2() },
            tileMask: { value: 15 },
            colorUvRect: { value: new THREE.Vector4(0, 0, 1, 1) },
            tileMeters: { value: 1 },
            heightScale: { value: 1 },
            zOffset: { value: 0 },
            baseZ: { value: 0 },
            skirtDepth: { value: 0 },
        },
    });
}

export type Extent = { minX: number; minY: number; maxX: number; maxY: number };

export interface LayerSource {
    id: string; // BASE_ID for the basemap, raster dataset id for overlays
    template: string; // raster tile URL with {z}/{x}/{y}
    maxZoom: number; // max zoom level of the raster tiles
    zoomBias: number; // levels the raster is fetched coarser than the mesh tile
    extentWm: Extent | null; // data extent (web-mercator), for root anchoring
    valueRange: [number, number] | null; // non-null only if the tiles are terrarium-encoded
}

// How a value raster draws its values. Exclusive: contours drop the fill, thus a
// shaded relief under them would never show. The settings for the modes that are
// not picked stay put, thus switching back and forth does not reset them.
export type RasterRender = "default" | "hillshade" | "contours";

export const DEFAULT_RENDER: RasterRender = "default";

// Azimuth clockwise from north, altitude above the horizon, both in degrees, as
// in QGIS.
export interface HillshadeSettings {
    azimuth: number;
    altitude: number;
    exaggeration: number;
}

// Light from the north-west is the cartographic convention. Light from the other
// side shows the relief inverted.
export const DEFAULT_HILLSHADE: HillshadeSettings = {
    azimuth: 315,
    altitude: 45,
    exaggeration: 1,
};

// Isolines through the raster's values. interval is in the raster's own units;
// 0 means "not chosen", thus the stats-derived default is used. index draws
// every Nth line heavier, the ones a reader counts by; 0 or 1 turns that off.
export interface ContourSettings {
    interval: number;
    color: string;
}

const CONTOUR_TARGET_LINES = 20; // roughly, across the raster's range
const CONTOUR_FALLBACK = 10; // a raster with no min/max stats still has real values

export const DEFAULT_CONTOURS: ContourSettings = {
    interval: CONTOUR_FALLBACK,
    color: "#593314",
};

// sRGB 0..1 from "#rgb" or "#rrggbb", or null when the text is not a colour: the
// setting can hold anything, thus this has to reject. Any alpha suffix is
// dropped, the layer's own opacity already covers it.
function parseHexColor(hex: string): [number, number, number] | null {
    const match = /^#?([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(hex.trim());
    if (!match) return null;

    const digits = match[1];
    const short = digits.length <= 4;
    const width = short ? 1 : 2;
    const channel = (i: number) => {
        const part = digits.slice(i * width, i * width + width);
        return parseInt(short ? part + part : part, 16) / 255;
    };
    return [channel(0), channel(1), channel(2)];
}

const DEFAULT_CONTOUR_RGB = parseHexColor(DEFAULT_CONTOURS.color) as [number, number, number];

// A 1/2/5 x 10^k interval giving about CONTOUR_TARGET_LINES lines over the range.
function autoInterval(range: [number, number]): number {
    const span = range[1] - range[0];
    if (!(span > 0)) return CONTOUR_FALLBACK;

    const raw = span / CONTOUR_TARGET_LINES;
    const magnitude = 10 ** Math.floor(Math.log10(raw));
    const steps = [1, 2, 5, 10];
    const step = steps.find((s) => raw <= s * magnitude) ?? 10;
    return step * magnitude;
}

// A data raster's tile source: an XYZ URL template ({z}/{x}/{y}) plus the zoom
// range and extent used to overzoom and anchor it.
interface RasterConfig {
    id: string;
    urlTemplate: string;
    maxZoom: number;
    boundsWgs84?: [number, number, number, number]; // data extent, for root anchoring
    valueRange: [number, number] | null;
}

// bounds (WGS84) -> web-mercator extent, for anchoring the tree roots.
function boundsToExtentWm(bounds?: [number, number, number, number]): Extent | null {
    if (!bounds) return null;
    const [west, south, east, north] = bounds;
    const min = lonLatToMercator(west, south);
    const max = lonLatToMercator(east, north);
    return { minX: min.x, minY: min.y, maxX: max.x, maxY: max.y };
}

function extentsIntersect(a: Extent, b: Extent): boolean {
    return a.minX < b.maxX && a.maxX > b.minX && a.minY < b.maxY && a.maxY > b.minY;
}

// The server sends single-band non-byte rasters terrarium-encoded, not as a
// picture. Returns the display stretch, or null for imagery. The stretch has
// zero width if the raster has no min/max stats.
export function valueRange(bandInfo: RasterBandInfo[]): [number, number] | null {
    const bands = bandInfo.filter((b) => b.name !== "Alpha");
    const band = bands[0];
    if (bands.length !== 1 || !band || band.dtype === "byte") return null;
    return [band.min ?? 0, band.max ?? 0];
}

// For LumiDB rasters, derive the max zoom from metadata already loaded by App so
// full-resolution tiles load when zoomed in; other URLs keep the default cap.
function rasterConfig(
    urlTemplate: string,
    id: string,
    boundsWgs84?: [number, number, number, number],
    meta?: RasterMetadata,
): RasterConfig {
    let maxZoom = 16;
    const range = meta ? valueRange(meta.band_info) : null;
    if (meta) {
        const lonSpan = Math.abs(meta.bounds_wgs84[2] - meta.bounds_wgs84[0]);
        if (lonSpan > 0 && meta.width_px > 0) {
            // cos(latitude) cancels, leaving lon span and pixel width.
            const zoom = Math.ceil(Math.log2((360 * meta.width_px) / (256 * lonSpan)));
            maxZoom = Math.min(24, Math.max(16, zoom));
        }
    }

    return { id, urlTemplate, maxZoom, boundsWgs84, valueRange: range };
}

interface SceneBox {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
    minZ: number | null; // minimum DTM elevation in metres
    maxZ: number | null; // maximum DTM elevation in metres
    exact: boolean; // Z from the tile's own loaded DTM grid rather than sampled
}

interface ColorBinding {
    texture: THREE.Texture | null;
    uvRect: THREE.Vector4;
    tileMeters: number;
}

// Imagery and elevations refine independently of mesh selection. Materials and
// raster textures are shared, while each tile layer owns its render mesh.
interface TileLayer {
    status: "pending" | "loading" | "ready" | "failed";
    attempts: number;
    binding: ColorBinding;
    mesh: THREE.Mesh | null;
}

type TileSelection = "covered" | "culled" | "unavailable";

interface Tile {
    layers: Map<string, TileLayer>; // keyed by layer id (BASE_ID + overlay ids)
    coords: { z: number; x: number; y: number };
    box: SceneBox;
    anchor: THREE.Vector2; // positions are relative to this anchor to retain float precision
    geometry: THREE.BufferGeometry;
    depthMesh: THREE.Mesh | null;
    vertices: Float32Array; // local XY and elevation per grid vertex
    skirtDepth: number; // metres, before heightScale
    dtmGrid: dtm.DtmGrid | null;
    heightRevision: number; // revision baked into vertex elevations
    coverage: number; // quadrant bitmask for partial geometry refinement
    dtmStatus: "pending" | "loading" | "ready" | "failed";
    dtmAttempts: number;
    lastUsed: number; // frame clock when selection last needed this tile
    lastWanted: number; // frame clock when the desired-tile traversal last reached this tile
    priority: number; // current span / camera-distance score, refreshed by each walk
}

const STALE_REQUEST = new Error("stale terrain request");

type NetworkRequest = {
    start: () => void;
    cancel: () => void;
    isRelevant: () => boolean;
    priority: () => number;
};

// Min/max elevation for culling.
function heightStats({ data }: dtm.DtmGrid): { min: number; max: number } {
    let min = Infinity;
    let max = -Infinity;
    for (const h of data) {
        min = Math.min(min, h);
        max = Math.max(max, h);
    }
    return { min, max };
}

export class Terrain {
    state = proxy({
        type: "auto" as BasemapType,
        terrain: true,
        zOffsetM: DEFAULT_Z_OFFSET_M,
        render: {} as Record<string, RasterRender>,
        hillshade: {} as Record<string, HillshadeSettings>,
        contours: {} as Record<string, ContourSettings>,
        opacity: {} as Record<string, number>,
        stats: {
            fetching: 0,
            loaded: 0,
            queued: 0,
        },
    });
    readonly group = new THREE.Group();
    private grid = buildGrid(GRID_N);
    private baseMaterial = createTerrainMaterial();
    private depthMaterial: THREE.RawShaderMaterial | null = null;
    private layerMaterials = new Map<string, THREE.RawShaderMaterial>();
    private renderedMeshes = new Set<THREE.Mesh>();
    private tiles = new Map<string, Tile>();
    private boxes = new Map<string, SceneBox | null>(); // null = outside the projection domain
    private rasterLoads = new Map<string, Promise<THREE.Texture | null>>();
    private rasterTiles = new Map<string, THREE.Texture>();

    private baseExtent: Extent = DEFAULT_EXTENT;
    private baseSource: LayerSource | null = null;
    private layerSources: LayerSource[] = [];
    private rasterConfigs = new Map<string, RasterConfig>();
    private roots: { z: number; x: number; y: number }[] = [];
    private rootKeys = new Set<string>();
    private heightScale = 1;
    private zOffset = 0;
    private generation = 0;
    private activeRequests = 0;
    private requestQueue = new PriorityQueue<NetworkRequest>((request) => request.priority());
    private loadAbort = new AbortController();
    private evalTimeoutHandle: number | undefined = undefined;
    private evalAt = performance.now();

    private loaded = 0;

    private updateStats = throttle(() => {
        this.state.stats.queued = this.requestQueue.size;
        this.state.stats.fetching = this.activeRequests;
        this.state.stats.loaded = this.loaded;
    });

    private dequeueRequest(): NetworkRequest | null {
        while (true) {
            const request = this.requestQueue.dequeue();
            if (!request) return null;
            if (request.isRelevant()) return request;
            request.cancel();
        }
    }

    private clearRequests() {
        while (true) {
            const request = this.requestQueue.dequeue();
            if (!request) break;
            request.cancel();
        }
    }

    private runRequest<T>(request: () => Promise<T>, priority: () => number, isRelevant: () => boolean): Promise<T> {
        return new Promise((resolve, reject) => {
            const onfinally = () => {
                this.activeRequests--;
                const next = this.dequeueRequest();
                if (next) next.start();
            };

            const start = () => {
                this.activeRequests++;
                request().then(resolve, reject).finally(onfinally);
            };

            if (this.activeRequests < MAX_CONCURRENT_REQUESTS) {
                start();
            } else {
                this.requestQueue.enqueue({
                    start,
                    cancel: () => reject(STALE_REQUEST),
                    isRelevant,
                    priority,
                });
            }
        });
    }

    // A custom XYZ basemap (type === "custom"); set via setBaseUrl.
    private customConfig: RasterConfig | null = null;
    private visible = new Set<Tile>();
    private frameTime = 0; // performance.now() at the start of the current selection
    private frustum = new THREE.Frustum();
    private tmp = {
        viewProj: new THREE.Matrix4(),
        tileBounds: new THREE.Box3(),
    };

    private readonly app: App;
    private readonly auth: Auth;
    private readonly viewer: Viewer;
    private readonly project: Project;

    constructor(
        viewer: Viewer,
        app: App,
        project: Project,
        auth: Auth,
        type: Exclude<BasemapType, "custom" | "none"> = "auto",
    ) {
        this.viewer = viewer;
        this.app = app;
        this.project = project;
        this.auth = auth;
        this.state.type = type;
        this.viewer.scene.add(this.group);
        this.viewer.terrain = this;

        window.addEventListener("online", () => this.retryFailedLoads());

        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        mediaQuery.addEventListener("change", () => {
            if (this.state.type === "auto") {
                this.init();
            }
        });
    }

    // Data extent reprojected to web-mercator, for anchoring the tree roots.
    private extentWm(): Extent | null {
        const { project_id } = this.project.get();
        const extent = this.project.extent(project_id);

        const size = extent.getSize(new THREE.Vector3());
        if (size.x <= 1 && size.y <= 1) return null; // empty / unit-box fallback

        const corners = [
            this.app.toWm(extent.min.x, extent.min.y),
            this.app.toWm(extent.max.x, extent.min.y),
            this.app.toWm(extent.min.x, extent.max.y),
            this.app.toWm(extent.max.x, extent.max.y),
        ];

        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        for (const [x, y] of corners) {
            if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
            minX = Math.min(minX, x);
            maxX = Math.max(maxX, x);
            minY = Math.min(minY, y);
            maxY = Math.max(maxY, y);
        }

        return { minX, minY, maxX, maxY };
    }

    setType(type: Exclude<BasemapType, "custom">) {
        this.state.type = type;
        this.init();
    }

    // Point the basemap at a custom XYZ template.
    async setBaseUrl(url: string) {
        this.customConfig = rasterConfig(url, BASE_ID);
        this.state.type = "custom";

        return this.init();
    }

    public async init() {
        const extentWm = this.extentWm();
        const token = this.reset();
        if (!extentWm) return;

        this.updateTextures();
        if (token !== this.generation) return;

        this.layerSources = this.getLayerSources();
        this.heightScale = this.terrainHeightScale();
        this.zOffset = this.state.zOffsetM * this.metersToSceneZ;
        this.updateBaseSource(extentWm);

        this.doEval();

        while (token === this.generation && this.isLoading()) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
        }
    }

    private updateTextures() {
        const rasters = this.project.rasterMetadata();

        for (const id of [...this.rasterConfigs.keys()]) {
            if (!rasters[id]) this.rasterConfigs.delete(id);
        }

        for (const [id, meta] of Object.entries(rasters)) {
            if (meta.bounds_wgs84) {
                const boundsWgs84 = meta.bounds_wgs84.slice(0, 4) as [number, number, number, number];
                const name = encodeURIComponent(meta.name);
                const urlTemplate = `${window.location.origin}/api/rasters/${name}/tiles/{z}/{x}/{y}`;
                const config = rasterConfig(urlTemplate, id, boundsWgs84, this.app.state.rasters[id]);
                this.rasterConfigs.set(id, config);

                if (config.valueRange && !this.state.contours[id]) {
                    const interval = autoInterval(config.valueRange);
                    this.state.contours[id] = { ...DEFAULT_CONTOURS, interval };
                }
            }
        }
    }

    // Ordered overlay sources. The scene tree lists the topmost layer first; slot
    // 0 paints first (bottom), so we emit bottom→top. Hidden rasters stay in the
    // list so existing tile layers can be toggled without being removed/re-added.
    private getLayerSources(): LayerSource[] {
        const sceneTree = this.project.get().scene_tree ?? [];
        const leaves = sceneTree.flatMap((node) => (node.kind === "group" ? node.children : [node]));
        const treeOrder = leaves.flatMap((node) => (node.kind === "raster" ? [node.id] : []));
        const treeSet = new Set(treeOrder);

        // Tree order is top→bottom; reverse for bottom→top slot order.
        const inTree = treeOrder.filter((id) => this.rasterConfigs.has(id)).reverse();
        const orphans = [...this.rasterConfigs.keys()].filter((id) => !treeSet.has(id));

        return [...orphans, ...inTree].map((id) => this.toLayerSource(this.rasterConfigs.get(id) as RasterConfig));
    }

    private toLayerSource(config: RasterConfig): LayerSource {
        return {
            id: config.id,
            zoomBias: 0,
            template: config.urlTemplate,
            maxZoom: config.maxZoom,
            extentWm: boundsToExtentWm(config.boundsWgs84),
            valueRange: config.valueRange,
        };
    }

    toggleTerrain() {
        this.state.terrain = !this.state.terrain;
        this.applyHeightScale();
        this.eval();
        this.viewer.update();
    }

    getHeightAtScreenPosition(screenPosition: THREE.Vector2, output?: THREE.Vector3): THREE.Vector3 | null {
        const ray = this.viewer.getMouseRay(screenPosition);
        const hit = this.raycast(ray);
        if (!hit) return null;
        return output ? output.copy(hit) : hit;
    }

    // Drop tile geometry, material and textures
    public reset() {
        const token = ++this.generation;

        clearTimeout(this.evalTimeoutHandle);
        this.evalTimeoutHandle = undefined;
        this.loadAbort.abort();
        this.loadAbort = new AbortController();
        this.clearRequests();

        this.disposeRenderResources();

        for (const texture of this.rasterTiles.values()) this.disposeRasterTile(texture);

        this.group.clear();
        this.tiles.clear();
        this.visible.clear();
        this.boxes.clear();
        this.rasterTiles.clear();
        this.rasterLoads.clear();

        this.baseSource = null;
        this.baseExtent = DEFAULT_EXTENT;
        this.roots.length = 0;
        this.rootKeys.clear();
        this.layerSources.length = 0;

        this.loaded = 0;
        this.updateStats.cancel();
        this.state.stats.queued = 0;
        this.state.stats.fetching = 0;
        this.state.stats.loaded = 0;

        return token;
    }

    // Transparent copy of base material using premultiplied alpha and hillshade not baked.
    private makeRasterMaterial(): THREE.RawShaderMaterial {
        const material = this.baseMaterial.clone();
        material.transparent = true;
        material.premultipliedAlpha = true; // the shader premultiplies; blend must not do it again
        material.depthWrite = false;
        material.uniforms.shade.value = 0;
        material.uniforms.overlay.value = 1;
        return material;
    }

    private disposeRenderResources() {
        for (const tile of this.tiles.values()) tile.geometry.dispose();
        this.depthMaterial?.dispose();
        for (const material of this.layerMaterials.values()) material.dispose();
        this.layerMaterials.clear();
        this.renderedMeshes.clear();
        this.depthMaterial = null;
    }

    private layerRenderOrder(id: string): number {
        if (id === BASE_ID) return 0;
        const index = this.layerSources.findIndex((source) => source.id === id);
        return index < 0 ? 1 : index + 1;
    }

    private getDepthMaterial(): THREE.RawShaderMaterial {
        if (!this.depthMaterial) {
            this.depthMaterial = createDepthMaterial();
            this.depthMaterial.uniforms.heightScale.value = this.heightScale;
            this.depthMaterial.uniforms.zOffset.value = this.zOffset;
            this.depthMaterial.uniforms.baseZ.value = this.app.state.offset.z;
        }
        return this.depthMaterial;
    }

    private getLayerMaterial(id: string): THREE.RawShaderMaterial {
        let material = this.layerMaterials.get(id);
        if (material) return material;
        material = id === BASE_ID ? this.baseMaterial.clone() : this.makeRasterMaterial();
        material.uniforms.heightScale.value = this.heightScale;
        material.uniforms.zOffset.value = this.zOffset;
        material.uniforms.baseZ.value = this.app.state.offset.z;
        material.uniforms.split.value = this.splitFor(id);
        material.uniforms.opacity.value = this.state.opacity[id] ?? 1;
        const source = this.sourceFor(id);
        if (source?.valueRange) this.applyValueUniforms(material, source);
        this.layerMaterials.set(id, material);
        return material;
    }

    private applyTileUniforms(material: THREE.RawShaderMaterial, tile: Tile, binding?: ColorBinding) {
        material.uniforms.anchor.value = tile.anchor;
        material.uniforms.tileMask.value = tile.coverage;
        material.uniforms.skirtDepth.value = tile.skirtDepth;
        if (binding) {
            material.uniforms.colorTex.value = binding.texture;
            material.uniforms.colorUvRect.value = binding.uvRect;
            material.uniforms.tileMeters.value = binding.tileMeters;
        }
        material.uniformsNeedUpdate = true;
    }

    private getDepthMesh(tile: Tile): THREE.Mesh {
        if (tile.depthMesh) return tile.depthMesh;
        const material = this.getDepthMaterial();
        const mesh = new THREE.Mesh(tile.geometry, material);
        mesh.layers.set(LAYERS.terrain);
        mesh.frustumCulled = false;
        mesh.renderOrder = -1;
        mesh.visible = false;
        mesh.onBeforeRender = () => this.applyTileUniforms(material, tile);
        this.group.add(mesh);
        tile.depthMesh = mesh;
        return mesh;
    }

    private getLayerMesh(tile: Tile, id: string, layer: TileLayer): THREE.Mesh {
        if (layer.mesh) return layer.mesh;
        const material = this.getLayerMaterial(id);
        const mesh = new THREE.Mesh(tile.geometry, material);
        mesh.layers.set(LAYERS.terrain);
        mesh.frustumCulled = false;
        mesh.renderOrder = this.layerRenderOrder(id);
        mesh.visible = false;
        mesh.onBeforeRender = () => this.applyTileUniforms(material, tile, layer.binding);
        this.group.add(mesh);
        layer.mesh = mesh;
        return mesh;
    }

    private disposeLayerMaterial(id: string) {
        this.layerMaterials.get(id)?.dispose();
        this.layerMaterials.delete(id);
    }

    // Union of the base extent and every overlay's extent, so the root set covers
    // rasters that reach beyond the basemap's project extent.
    private extent(): Extent {
        const e = { ...this.baseExtent };
        for (const l of this.layerSources) {
            if (!l.extentWm || !this.layerVisible(l.id)) continue;
            e.minX = Math.min(e.minX, l.extentWm.minX);
            e.minY = Math.min(e.minY, l.extentWm.minY);
            e.maxX = Math.max(e.maxX, l.extentWm.maxX);
            e.maxY = Math.max(e.maxY, l.extentWm.maxY);
        }
        return e;
    }

    private clampWm(v: number) {
        return Math.min(Math.max(v, -ORIGIN_SHIFT + 1), ORIGIN_SHIFT - 1);
    }

    private tileForWMX(wmX: number, n: number) {
        return Math.min(n - 1, Math.max(0, Math.floor(((wmX + ORIGIN_SHIFT) / WORLD_SIZE) * n)));
    }

    private tileForWMY(wmY: number, n: number) {
        return Math.min(n - 1, Math.max(0, Math.floor(((ORIGIN_SHIFT - wmY) / WORLD_SIZE) * n)));
    }

    private rootsForExtent(extent: Extent) {
        const z = BASE_ZOOM;
        const n = 2 ** z;
        const pad = WORLD_SIZE / n / 4;

        const minX = this.tileForWMX(this.clampWm(extent.minX - pad), n);
        const maxX = this.tileForWMX(this.clampWm(extent.maxX + pad), n);
        const minY = this.tileForWMY(this.clampWm(extent.maxY + pad), n);
        const maxY = this.tileForWMY(this.clampWm(extent.minY - pad), n);

        const roots: { z: number; x: number; y: number }[] = [];
        for (let x = minX; x <= maxX; x++) {
            for (let y = minY; y <= maxY; y++) {
                roots.push({ z, x, y });
            }
        }
        return roots;
    }

    private updateRoots() {
        this.roots = this.rootsForExtent(this.extent());
        this.rootKeys = new Set(this.roots.map(({ z, x, y }) => tileKey(z, x, y)));
    }

    private getBaseSource(
        template: string,
        maxZoom: number,
        retina = false,
        valueRange: [number, number] | null = null,
    ): LayerSource {
        return {
            template,
            maxZoom,
            valueRange,
            id: BASE_ID,
            extentWm: this.baseExtent,
            zoomBias: retina ? 2 : 1,
        };
    }

    private updateBaseSource(extentWm: Extent) {
        this.baseExtent = extentWm;

        const type = this.state.type;
        if (type === "none") {
            this.baseSource = null;
        } else if (type === "custom") {
            const config = this.customConfig;
            if (!config) {
                this.baseSource = null;
            } else {
                this.baseSource = this.getBaseSource(config.urlTemplate, config.maxZoom, false, config.valueRange);
            }
        } else if (type === "esri") {
            this.baseSource = this.getBaseSource(ESRI_TEMPLATE, 19);
        } else {
            if (!MAPTILER_KEY) {
                this.baseSource = null;
            } else {
                const style = getMaptilerStyle(type);
                const url = "https://api.maptiler.com";
                const path = `/maps/${style}/{z}/{x}/{y}${RETINA ? "@2x" : ""}.png`;
                const template = `${url}${path}?key=${MAPTILER_KEY}`;

                this.baseSource = this.getBaseSource(template, 20, RETINA);
            }
        }

        this.updateRoots();
        this.viewer.update();
    }

    // Re-sync textures and layers from the raster catalogue (slot 0 = bottom of the
    // stack). Hidden layers are retained and only stop rendering/loading.
    update() {
        this.updateTextures();
        this.layerSources = this.getLayerSources();
        const next = new Map(this.layerSources.map((l) => [l.id, l]));

        const removed = [...this.layerMaterials.keys()].filter((id) => id !== BASE_ID && !next.has(id));

        for (const tile of this.tiles.values()) {
            for (const id of [...tile.layers.keys()]) {
                if (id !== BASE_ID && !next.has(id)) {
                    this.removeLayer(tile, id);
                }
            }

            for (const l of this.layerSources) {
                if (!tile.layers.has(l.id) && this.shouldLoadLayer(tile, l.id)) this.addLayer(tile, l.id);
            }
        }

        for (const id of removed) this.disposeLayerMaterial(id);
        for (const tile of this.tiles.values()) {
            for (const [id, layer] of tile.layers) {
                if (layer.mesh) layer.mesh.renderOrder = this.layerRenderOrder(id);
            }
        }

        this.updateRoots();
        this.eval();
    }

    // Raster textures and materials are shared; only drop tile-local state and mesh.
    private removeLayer(tile: Tile, id: string): void {
        const layer = tile.layers.get(id);
        if (!layer) return;
        if (layer.status === "ready") {
            this.loaded--;
            this.updateStats();
        }
        if (layer.mesh) {
            this.renderedMeshes.delete(layer.mesh);
            this.group.remove(layer.mesh);
        }
        tile.layers.delete(id);
    }

    private addLayer(tile: Tile, id: string) {
        tile.layers.set(id, {
            status: "pending",
            attempts: 0,
            mesh: null,
            binding: {
                texture: null,
                uvRect: new THREE.Vector4(0, 0, 1, 1),
                tileMeters: 1,
            },
        });
    }

    public eval() {
        const dt = performance.now() - this.evalAt;

        clearTimeout(this.evalTimeoutHandle);
        this.evalTimeoutHandle = undefined;

        if (dt > EVAL_INTERVAL_MS) {
            this.doEval();
        } else {
            this.evalTimeoutHandle = window.setTimeout(() => {
                this.evalTimeoutHandle = undefined;
                this.doEval();
            }, EVAL_INTERVAL_MS - dt);
        }
    }

    private doEval() {
        clearTimeout(this.evalTimeoutHandle);
        this.evalTimeoutHandle = undefined;
        this.evalAt = performance.now();
        this.frameTime = this.evalAt;

        this.updateFrustum();

        this.walkTiles();
        this.selectTiles();
        this.updateTileHeights();
        this.sweep();

        this.showTiles();
        this.updateStats();
        this.viewer.update();
    }

    private showTiles() {
        for (const mesh of this.renderedMeshes) mesh.visible = false;
        this.renderedMeshes.clear();

        for (const tile of this.visible) {
            let hasLayer = false;
            for (const [id, layer] of tile.layers) {
                if (!this.layerVisible(id) || !this.bindColor(tile, id, layer) || !layer.binding.texture) continue;
                const mesh = this.getLayerMesh(tile, id, layer);
                mesh.visible = true;
                this.renderedMeshes.add(mesh);
                hasLayer = true;
            }
            if (hasLayer) {
                const mesh = this.getDepthMesh(tile);
                mesh.visible = true;
                this.renderedMeshes.add(mesh);
            }
        }
    }

    // Keep sampling the finest loaded heights; skirts hide differences between
    // the interpolated edges of different mesh resolutions.
    private updateTileHeights(): void {
        const count = this.grid.uv.length / 2;
        const elevationRevision = dtm.getElevationRevision();
        for (const tile of this.visible) {
            if (tile.heightRevision === elevationRevision) {
                continue;
            }

            tile.heightRevision = elevationRevision;

            const { z, x, y } = tile.coords;
            const scale = 2 ** z;

            // A skirt vertex shares its twin's UV, so it samples the same height;
            // the shader lowers it by skirtDepth to hang the strip below the edge.
            let minZ = Infinity;
            let maxZ = -Infinity;
            for (let i = 0; i < count; i++) {
                const u = (x + this.grid.uv[i * 2]) / scale;
                const v = (y + this.grid.uv[i * 2 + 1]) / scale;
                const h = dtm.sampleElevationUv(u, v) ?? 0;
                tile.vertices[i * 3 + 2] = h;
                minZ = Math.min(minZ, h);
                maxZ = Math.max(maxZ, h);
            }

            tile.geometry.getAttribute("position").needsUpdate = true;
            tile.geometry.computeVertexNormals(); // skirts stay degenerate; the shader lights them flat
            tile.skirtDepth = Math.max(10, maxZ - minZ);
            tile.box.minZ = minZ;
            tile.box.maxZ = maxZ;
        }
    }

    // Finest loaded texture covering this tile for the source, or null. A
    // pending/failed DTM, sibling or other overlay never blocks this one.
    private rasterTextureFor(
        tile: Tile,
        source: LayerSource,
    ): { texture: THREE.Texture; z: number; x: number; y: number; scale: number } | null {
        const r = this.rasterCoords(tile, source);
        for (let i = 0; i <= r.z; i++) {
            const z = r.z - i;
            const scale = 2 ** (tile.coords.z - z);
            const x = Math.floor(tile.coords.x / scale);
            const y = Math.floor(tile.coords.y / scale);
            const url = this.rasterUrl(source.template, z, x, y);
            const texture = this.rasterTiles.get(url);
            if (texture) return { texture, z, x, y, scale };
        }
        return null;
    }

    private bindColor(tile: Tile, id: string, layer: TileLayer): boolean {
        const source = this.sourceFor(id);
        const found = source && this.rasterTextureFor(tile, source);
        if (!source || !found) {
            layer.binding.texture = null;
            return false;
        }

        const { texture, z, x, y, scale } = found;
        layer.binding.texture = texture;
        layer.binding.uvRect.set(
            (tile.coords.x % scale) / scale,
            (tile.coords.y % scale) / scale,
            1 / scale,
            1 / scale,
        );
        layer.binding.tileMeters = 1;
        if (source.valueRange) {
            const extent = getTileExtent(z, x, y);
            const { lat } = mercatorToLonLat(extent.minX, (extent.minY + extent.maxY) / 2);
            layer.binding.tileMeters = extent.span * Math.cos((lat * Math.PI) / 180);
        }
        return true;
    }

    // Scene-space XY extent of a slippy tile, reprojected from its mercator
    // corners. null when any corner falls outside the projection's valid domain.
    private makeTileSceneBox(z: number, x: number, y: number): SceneBox | null {
        const tileExtent = getTileExtent(z, x, y);

        const corners = [
            this.app.fromWm(tileExtent.minX, tileExtent.minY),
            this.app.fromWm(tileExtent.maxX, tileExtent.minY),
            this.app.fromWm(tileExtent.minX, tileExtent.maxY),
            this.app.fromWm(tileExtent.maxX, tileExtent.maxY),
        ];

        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        for (const [px, py] of corners) {
            if (!Number.isFinite(px) || !Number.isFinite(py)) return null;
            minX = Math.min(minX, px);
            maxX = Math.max(maxX, px);
            minY = Math.min(minY, py);
            maxY = Math.max(maxY, py);
        }

        const box: SceneBox = {
            minX: minX - this.app.state.offset.x,
            minY: minY - this.app.state.offset.y,
            maxX: maxX - this.app.state.offset.x,
            maxY: maxY - this.app.state.offset.y,
            minZ: null,
            maxZ: null,
            exact: false,
        };

        this.updateTileSceneBoxHeights(z, x, y, box);
        return box;
    }

    // Sampled heights improve as DTM sources load, so the walk refreshes them.
    private updateTileSceneBoxHeights(z: number, x: number, y: number, box: SceneBox): void {
        if (box.exact) return;

        // A child lies within its parent, so the parent's exact range bounds it.
        const parent = this.boxes.get(tileKey(z - 1, Math.floor(x / 2), Math.floor(y / 2)));
        if (parent?.exact) {
            box.minZ = parent.minZ;
            box.maxZ = parent.maxZ;
            return;
        }

        const extent = getTileExtent(z, x, y);
        let minZ = Infinity;
        let maxZ = -Infinity;

        for (let gy = 0; gy <= BOX_HEIGHT_GRID_N; gy++) {
            const wmY = extent.minY + (gy / BOX_HEIGHT_GRID_N) * (extent.maxY - extent.minY);

            for (let gx = 0; gx <= BOX_HEIGHT_GRID_N; gx++) {
                const wmX = extent.minX + (gx / BOX_HEIGHT_GRID_N) * (extent.maxX - extent.minX);

                const elevation = dtm.sampleElevation(wmX, wmY);
                if (elevation === null || !Number.isFinite(elevation)) {
                    continue;
                }

                minZ = Math.min(minZ, elevation);
                maxZ = Math.max(maxZ, elevation);
            }
        }

        box.minZ = minZ === Infinity ? null : minZ;
        box.maxZ = maxZ === -Infinity ? null : maxZ;
    }

    private tileSceneBox(z: number, x: number, y: number): SceneBox | null {
        const k = tileKey(z, x, y);
        if (this.boxes.has(k)) return this.boxes.get(k) ?? null;

        const box = this.makeTileSceneBox(z, x, y);
        this.boxes.set(k, box);
        return box;
    }

    public getRootBounds(): THREE.Box3[] {
        const extentWm = this.extentWm();
        if (!extentWm) return [];

        const bounds: THREE.Box3[] = [];

        for (const root of this.rootsForExtent(extentWm)) {
            const box = this.tileSceneBox(root.z, root.x, root.y);
            if (!box) continue;
            const [minZ, maxZ] = this.sceneZBounds(box);

            bounds.push(
                new THREE.Box3(
                    new THREE.Vector3(box.minX, box.minY, minZ),
                    new THREE.Vector3(box.maxX, box.maxY, maxZ),
                ),
            );
        }

        return bounds;
    }

    /** Basemap elevations are metres. Folded into `heightScale` so the shader converts too. */
    private get metersToSceneZ(): number {
        return metersToScene(1, this.app.state.sceneUnit);
    }

    private terrainHeightScale(): number {
        return (this.state.terrain ? 1 : 0) * this.metersToSceneZ;
    }

    private worldZ(elevation: number): number {
        return elevation * this.heightScale + this.zOffset - this.app.state.offset.z;
    }

    private updateFrustum() {
        const camera = this.viewer.camera;
        camera.updateMatrixWorld(true);
        this.tmp.viewProj.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
        this.tmp.viewProj.premultiply(MARGIN_SCALE);
        this.frustum.setFromProjectionMatrix(this.tmp.viewProj);
    }

    private sceneZBounds(box: SceneBox): [number, number] {
        if (box.minZ === null || box.maxZ === null) {
            const height = Math.max(box.maxX - box.minX, box.maxY - box.minY);
            const centerZ = this.worldZ(0);
            return [centerZ - height / 2, centerZ + height / 2];
        }

        const minZ = this.worldZ(box.minZ);
        const maxZ = this.worldZ(box.maxZ);
        return [Math.min(minZ, maxZ), Math.max(minZ, maxZ)];
    }

    private intersectsFrustum(box: SceneBox): boolean {
        const [minZ, maxZ] = this.sceneZBounds(box);
        this.tmp.tileBounds.min.set(box.minX, box.minY, minZ);
        this.tmp.tileBounds.max.set(box.maxX, box.maxY, maxZ);
        return this.frustum.intersectsBox(this.tmp.tileBounds);
    }

    // Queue requests for visible tiles
    private walkTiles(): void {
        const visit = (z: number, x: number, y: number) => {
            const box = this.tileSceneBox(z, x, y);
            if (!box) return;

            this.updateTileSceneBoxHeights(z, x, y, box);
            if (!this.intersectsFrustum(box)) return;

            const tile = this.ensureTile(z, x, y, box);
            if (!tile) return;

            const span = Math.max(box.maxX - box.minX, box.maxY - box.minY);
            const dist = Math.max(1, this.cameraDistance(box));
            this.queue(tile, span / dist);

            if (!Number.isFinite(span) || z >= MAX_TILE_ZOOM || dist >= span * SPLIT_FACTOR) {
                return;
            }

            for (let oy = 0; oy < 2; oy++) {
                for (let ox = 0; ox < 2; ox++) {
                    visit(z + 1, x * 2 + ox, y * 2 + oy);
                }
            }
        };

        for (const { z, x, y } of this.roots) {
            visit(z, x, y);
        }

        this.requestQueue.reprioritize();
    }

    private selectTiles() {
        this.visible.clear();

        for (const root of this.roots) {
            this.selectTile(root);
        }
    }

    private selectTile(coords: { z: number; x: number; y: number }): TileSelection {
        const box = this.tileSceneBox(coords.z, coords.x, coords.y);
        const tile = this.tiles.get(tileKey(coords.z, coords.x, coords.y));

        if (!box || !this.canRender(tile)) {
            return "unavailable";
        }

        if (!this.intersectsFrustum(box)) {
            return "culled";
        }

        tile.lastUsed = this.frameTime;

        const span = Math.max(box.maxX - box.minX, box.maxY - box.minY);
        const dist = Math.max(1, this.cameraDistance(box));

        // Mask the the tile with available children
        let mask = 15;
        if (coords.z < MAX_TILE_ZOOM && span > 0 && dist < span * SPLIT_FACTOR) {
            mask = 0;
            for (let oy = 0; oy < 2; oy++) {
                for (let ox = 0; ox < 2; ox++) {
                    const child = {
                        z: coords.z + 1,
                        x: coords.x * 2 + ox,
                        y: coords.y * 2 + oy,
                    };

                    // Keep the parent quadrant unless the child is actually selected
                    if (this.selectTile(child) !== "covered") {
                        mask |= 1 << (ox + 2 * oy);
                    }
                }
            }
        }

        tile.coverage = mask;
        if (mask !== 0) {
            this.visible.add(tile);
        }

        return "covered";
    }

    // Pure predicate: a texture is available for at least one visible layer.
    // Binding itself happens once per frame in showTiles, not here.
    private canRender(tile?: Tile): tile is Tile {
        if (tile) {
            for (const id of tile.layers.keys()) {
                if (!this.layerVisible(id)) continue;
                const source = this.sourceFor(id);
                if (source && this.rasterTextureFor(tile, source)) {
                    return true;
                }
            }
        }

        return false;
    }

    // Only load a layer for a tile if the layer and tile intersect
    private shouldLoadLayer(tile: Tile, layerId: string): boolean {
        const source = this.sourceFor(layerId);
        if (!source?.extentWm) return true;

        const { z, x, y } = tile.coords;
        const tileExtent = getTileExtent(z, x, y);
        return extentsIntersect(tileExtent, source.extentWm);
    }

    // Jittered so an outage does not retry every failed tile in the same frame.
    // Callers guard repend: retryFailedLoads may have revived the load already.
    private scheduleRetry(attempts: number, repend: () => void) {
        if (attempts >= MAX_LOAD_ATTEMPTS) return;
        setTimeout(
            () => {
                repend();
                this.eval();
            },
            RETRY_BASE_MS * 2 ** (attempts - 1) * (0.5 + Math.random()),
        );
    }

    // navigator.onLine stays true behind a captive portal or a dead uplink, so only
    // its transition event is worth acting on. Revives loads that hit MAX_LOAD_ATTEMPTS.
    private retryFailedLoads() {
        for (const tile of this.tiles.values()) {
            if (tile.dtmStatus === "failed") {
                tile.dtmAttempts = 0;
                tile.dtmStatus = "pending";
            }
            for (const layer of tile.layers.values()) {
                if (layer.status === "failed") {
                    layer.attempts = 0;
                    layer.status = "pending";
                }
            }
        }
        this.eval();
    }

    private queue(tile: Tile, priority: number) {
        tile.lastWanted = this.frameTime;
        tile.priority = priority;

        if (tile.dtmStatus === "pending") {
            // Mark before starting so another traversal cannot start the same tile.
            tile.dtmStatus = "loading";
            void this.startDtm(tile);
        }

        for (const [layerId, layer] of tile.layers) {
            if (layer.status === "pending" && this.layerVisible(layerId)) {
                layer.status = "loading";
                void this.startTexture(tile, layerId, layer);
            }
        }
    }

    // Nearest point of the tile's XY box, not its center, so a camera over an edge still splits it.
    private cameraDistance(box: SceneBox): number {
        const camera = this.viewer.camera;
        if (camera instanceof THREE.OrthographicCamera) {
            const orthoHeight = (camera.top - camera.bottom) / camera.zoom;
            return Math.max(1, orthoHeight / (2 * Math.tan(FOV_RAD / 2)));
        }

        const nx = Math.min(Math.max(camera.position.x, box.minX), box.maxX);
        const ny = Math.min(Math.max(camera.position.y, box.minY), box.maxY);
        const dx = camera.position.x - nx;
        const dy = camera.position.y - ny;
        const dz = Math.max(0, camera.position.z - this.worldZ(box.maxZ ?? 0));
        return Math.sqrt(dx * dx + dy * dy + dz * dz);
    }

    private ensureTile(z: number, x: number, y: number, box: SceneBox): Tile | null {
        const key = tileKey(z, x, y);
        const existing = this.tiles.get(key);
        if (existing) return existing;

        const tile = this.createTile(z, x, y, box);

        if (tile) {
            this.tiles.set(key, tile);
        } else {
            this.boxes.set(key, null);
        }

        return tile;
    }

    private sweep() {
        if (this.tiles.size <= MAX_TILES) {
            return;
        }

        let drops = 0;
        for (const [key, tile] of this.tiles) {
            if (drops >= MAX_DROPS_PER_SWEEP) {
                break;
            }

            const isRoot = this.rootKeys.has(key);
            const isLoading = this.tileIsLoading(tile);

            // Roots should never be dropped to avoid holes and
            // tiles should load before they can be dropped
            if (isRoot || isLoading) {
                continue;
            }

            if (this.frameTime - tile.lastUsed >= TILE_KEEP_MS) {
                this.dropTile(key, tile);
                drops++;
            }
        }
    }

    private tileIsLoading(tile: Tile): boolean {
        if (tile.dtmStatus === "loading") return true;
        for (const layer of tile.layers.values()) {
            if (layer.status === "loading") return true;
        }
        return false;
    }

    public isLoading(): boolean {
        // A pending eval may still discover and queue tiles.
        if (this.evalTimeoutHandle !== undefined) return true;
        if (this.activeRequests > 0 || this.requestQueue.size > 0) return true;
        for (const tile of this.tiles.values()) {
            if (this.tileIsLoading(tile)) return true;
        }
        return false;
    }

    // Raster textures are shared between tiles and not dropped
    private dropTile(key: string, tile: Tile) {
        for (const id of [...tile.layers.keys()]) {
            this.removeLayer(tile, id);
        }

        if (tile.dtmStatus === "ready") {
            this.loaded--;
            this.updateStats();
        }

        if (tile.depthMesh) {
            this.renderedMeshes.delete(tile.depthMesh);
            this.group.remove(tile.depthMesh);
        }
        tile.geometry.dispose();
        this.tiles.delete(key);
    }

    private createTile(z: number, x: number, y: number, box: SceneBox): Tile | null {
        const tileExtent = getTileExtent(z, x, y);
        const ox = this.app.state.offset.x;
        const oy = this.app.state.offset.y;
        const ax = (box.minX + box.maxX) / 2;
        const ay = (box.minY + box.maxY) / 2;

        const vertexCount = this.grid.uv.length / 2;
        const vertices = new Float32Array(vertexCount * 3);

        // Skirt vertices share a surface twin's UV, so they land on the same XY.
        for (let i = 0; i < vertexCount; i++) {
            const u = this.grid.uv[i * 2];
            const v = this.grid.uv[i * 2 + 1];
            const wmX = tileExtent.minX + u * (tileExtent.maxX - tileExtent.minX);
            const wmY = tileExtent.maxY - v * (tileExtent.maxY - tileExtent.minY); // v=0 is north
            const [sx, sy] = this.app.fromWm(wmX, wmY);
            if (![sx, sy].every(Number.isFinite)) return null;
            vertices[i * 3] = sx - ox - ax;
            vertices[i * 3 + 1] = sy - oy - ay;
        }

        const vertexAttribute = new THREE.BufferAttribute(vertices, 3).setUsage(THREE.DynamicDrawUsage);
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute("aUv", new THREE.BufferAttribute(this.grid.uv, 2));
        geometry.setAttribute("aSkirt", new THREE.BufferAttribute(this.grid.skirt, 1));
        geometry.setAttribute("position", vertexAttribute);
        geometry.setIndex(new THREE.BufferAttribute(this.grid.index, 1));
        geometry.computeVertexNormals();
        (geometry.getAttribute("normal") as THREE.BufferAttribute).setUsage(THREE.DynamicDrawUsage);

        const tile: Tile = {
            layers: new Map(),
            coords: { z, x, y },
            box: box,
            anchor: new THREE.Vector2(ax, ay),
            geometry,
            depthMesh: null,
            vertices,
            skirtDepth: 0,
            dtmGrid: null,
            coverage: 15,
            dtmStatus: "pending",
            dtmAttempts: 0,
            lastUsed: 0,
            lastWanted: 0,
            priority: 0,
            heightRevision: -1,
        };

        if (this.baseSource) this.addLayer(tile, BASE_ID);

        for (const l of this.layerSources) {
            if (this.shouldLoadLayer(tile, l.id)) this.addLayer(tile, l.id);
        }

        return tile;
    }

    private async startDtm(tile: Tile) {
        const generation = this.generation;
        try {
            const { z, x, y } = tile.coords;
            const grid = await dtm.sampleDtmGrid(
                z,
                x,
                y,
                GRID_N,
                (request) => {
                    return this.runRequest(
                        request,
                        () => tile.priority,
                        () => tile.lastWanted === this.frameTime,
                    );
                },
                (refined) => {
                    if (
                        generation !== this.generation ||
                        this.tiles.get(tileKey(z, x, y)) !== tile ||
                        tile.dtmGrid !== refined
                    ) {
                        return;
                    }
                    this.setTileHeights(tile, refined);
                    this.eval();
                },
            );

            if (generation !== this.generation) {
                return;
            }

            this.setTileHeights(tile, grid);
            tile.dtmStatus = "ready";
            this.loaded++;
            this.updateStats();
        } catch (error) {
            if (generation !== this.generation) {
                return;
            }

            if (error === STALE_REQUEST) {
                tile.dtmStatus = "pending";
            } else {
                tile.dtmStatus = "failed";
                this.scheduleRetry(++tile.dtmAttempts, () => {
                    if (tile.dtmStatus === "failed") tile.dtmStatus = "pending";
                });
            }
        }

        this.eval();
    }

    private setTileHeights(tile: Tile, grid: dtm.DtmGrid) {
        const { min, max } = heightStats(grid);
        tile.dtmGrid = grid;
        // A cached source can populate this grid without advancing the global
        // elevation revision. Force heights and normals to be rebuilt.
        tile.heightRevision = -1;
        tile.box.minZ = min;
        tile.box.maxZ = max;
        tile.box.exact = true;
    }

    private sourceFor(id: string): LayerSource | null {
        if (id === BASE_ID) return this.baseSource;
        return this.layerSources.find((l) => l.id === id) ?? null;
    }

    private layerVisible(id: string): boolean {
        return id === BASE_ID || this.app.state.visible.includes(id);
    }

    // Fetch only up to the raster's native resolution. Finer mesh tiles UV-map
    // to a sub-region of that image, or a loaded coarser ancestor, in bindColor.
    private rasterCoords(tile: Tile, source: LayerSource) {
        const { z, x, y } = tile.coords;
        const rZoom = Math.max(0, Math.min(z - source.zoomBias, source.maxZoom));
        const scale = 2 ** (z - rZoom);
        return { z: rZoom, x: Math.floor(x / scale), y: Math.floor(y / scale) };
    }

    private rasterUrl(template: string, z: number, x: number, y: number): string {
        return template.replaceAll("{z}", String(z)).replaceAll("{x}", String(x)).replaceAll("{y}", String(y));
    }

    private async startTexture(tile: Tile, layerId: string, layer: TileLayer) {
        const generation = this.generation;

        try {
            if (layerId !== BASE_ID && !this.app.state.rasters[layerId]) {
                await this.app.loadDatasetMeta(layerId);
                if (generation !== this.generation) return;

                this.updateTextures();
                this.layerSources = this.getLayerSources();
            }

            const source = this.sourceFor(layerId);
            if (!source) {
                layer.status = "pending";
                return;
            }

            const r = this.rasterCoords(tile, source);
            const raster = await this.loadRaster(
                source,
                r,
                () => tile.priority,
                () => tile.lastWanted === this.frameTime && this.layerVisible(layerId),
            );

            if (generation !== this.generation || tile.layers.get(layerId) !== layer) return;
            if (raster) {
                layer.status = "ready";
                this.loaded++;
                this.updateStats();
            } else {
                this.removeLayer(tile, layerId);
            }
        } catch (error) {
            if (generation !== this.generation || tile.layers.get(layerId) !== layer) return;

            if (error === STALE_REQUEST) {
                layer.status = "pending";
            } else {
                layer.status = "failed";
                this.scheduleRetry(++layer.attempts, () => {
                    if (layer.status === "failed") layer.status = "pending";
                });
            }
        }

        this.eval();
    }

    private applyValueUniforms(material: THREE.RawShaderMaterial, source: LayerSource) {
        const u = material.uniforms;
        const render = this.state.render[source.id] ?? DEFAULT_RENDER;

        const hs = { ...DEFAULT_HILLSHADE, ...this.state.hillshade[source.id] };
        u.values.value = 1;
        u.valueRange.value = source.valueRange ?? [0, 0];
        u.hillshade.value = render === "hillshade" ? 1 : 0;
        u.sun.value = [(hs.azimuth * Math.PI) / 180, (hs.altitude * Math.PI) / 180];
        u.exaggeration.value = hs.exaggeration;

        const ct = { ...DEFAULT_CONTOURS, ...this.state.contours[source.id] };
        u.contours.value = render === "contours" ? 1 : 0;
        u.contourInterval.value = ct.interval;
        u.contourColor.value = parseHexColor(ct.color) ?? DEFAULT_CONTOUR_RGB;
    }

    private async loadRaster(
        source: LayerSource,
        r: { z: number; x: number; y: number },
        priority: () => number,
        isRelevant: () => boolean,
    ): Promise<THREE.Texture | null> {
        const generation = this.generation;
        const url = this.rasterUrl(source.template, r.z, r.x, r.y);
        const encoded = !!source.valueRange;

        const cached = this.rasterLoads.get(url);
        if (cached) return cached;

        const pending = (async () => {
            // This is a URL-level cache miss. Keep the shared slot until the body
            // has downloaded, then release it while the image is decoded.
            const blob = await this.runRequest(
                async () => {
                    const res = await this.auth.fetch(url, {
                        signal: AbortSignal.any([this.loadAbort.signal, AbortSignal.timeout(FETCH_TIMEOUT_MS)]),
                    });

                    // Treat 400 and 404 as permanent misses, but allow other 4xx to retry.
                    if (res.status === 400 || res.status === 404) return null;
                    if (!res.ok) throw new Error(`raster ${url} -> ${res.status}`);

                    const body = await res.blob();
                    return res.status === 204 || body.size === 0 ? null : body;
                },
                priority,
                isRelevant,
            );
            if (!blob) return null;

            // Keep north at row 0 and packed value bytes untouched. Imagery is
            // straight-alpha sRGB; the fragment shader premultiplies its output.
            const bitmap = await createImageBitmap(blob, {
                imageOrientation: "none",
                premultiplyAlpha: "none",
                colorSpaceConversion: encoded ? "none" : "default",
            });
            const width = bitmap.width;
            const height = bitmap.height;
            let texture: THREE.Texture;
            if (encoded) {
                try {
                    texture = new THREE.DataTexture(
                        readImageBitmapPixels(bitmap),
                        width,
                        height,
                        THREE.RGBAFormat,
                        THREE.UnsignedByteType,
                    );
                } finally {
                    bitmap.close();
                }
                texture.colorSpace = THREE.NoColorSpace;
                texture.minFilter = texture.magFilter = THREE.NearestFilter;
                texture.generateMipmaps = false;
            } else {
                texture = new THREE.Texture(bitmap);
                texture.colorSpace = THREE.SRGBColorSpace;
                texture.minFilter = texture.magFilter = THREE.LinearFilter;
                texture.generateMipmaps = false;
                texture.anisotropy = Math.min(8, this.viewer.renderer?.capabilities.getMaxAnisotropy() ?? 1);
            }
            texture.flipY = false;
            texture.needsUpdate = true;
            if (generation === this.generation) {
                this.rasterTiles.set(url, texture);
                return texture;
            }
            this.disposeRasterTile(texture);
            return null;
        })();

        this.rasterLoads.set(url, pending);

        // A cached rejection would poison the url for the session. The identity
        // check keeps an entry that a reset() reloaded in the meantime.
        pending.catch(() => {
            if (this.rasterLoads.get(url) === pending) this.rasterLoads.delete(url);
        });

        return pending;
    }

    private disposeRasterTile(texture: THREE.Texture) {
        texture.dispose();
        const image = texture.image as ImageBitmap | undefined;
        image?.close?.();
    }

    setRender(rasterId: string, render: RasterRender) {
        this.state.render[rasterId] = render;
        this.applyToLayers(rasterId);
    }

    setHillshade(rasterId: string, patch: Partial<HillshadeSettings>) {
        this.state.hillshade[rasterId] = {
            ...DEFAULT_HILLSHADE,
            ...this.state.hillshade[rasterId],
            ...patch,
        };

        this.applyToLayers(rasterId);
    }

    setContours(rasterId: string, patch: Partial<ContourSettings>) {
        this.state.contours[rasterId] = {
            ...DEFAULT_CONTOURS,
            ...this.state.contours[rasterId],
            ...patch,
        };

        this.applyToLayers(rasterId);
    }

    private applyToLayers(rasterId: string) {
        const source = this.sourceFor(rasterId);
        if (!source?.valueRange) return;

        const material = this.layerMaterials.get(rasterId);
        if (material) this.applyValueUniforms(material, source);

        this.viewer.update();
    }

    // The tile bytes are the same at any opacity, thus a change moves uniforms
    // only and reloads no tiles.
    setOpacity(rasterId: string, value: number) {
        const opacity = Math.min(Math.max(value, 0), 1);
        this.state.opacity[rasterId] = opacity;

        const material = this.layerMaterials.get(rasterId);
        if (material) material.uniforms.opacity.value = opacity;

        this.viewer.update();
    }

    applyHeightScale() {
        this.heightScale = this.terrainHeightScale();
        this.zOffset = this.state.zOffsetM * this.metersToSceneZ;
        this.updateHeightUniforms();
    }

    setZOffset(meters: number) {
        this.state.zOffsetM = meters;
        this.zOffset = meters * this.metersToSceneZ;
        this.updateHeightUniforms();
        this.eval();
        this.viewer.update();
    }

    private updateHeightUniforms() {
        const baseZ = this.app.state.offset.z;
        const materials = [...this.layerMaterials.values()];
        if (this.depthMaterial) materials.push(this.depthMaterial);
        for (const material of materials) {
            material.uniforms.heightScale.value = this.heightScale;
            material.uniforms.zOffset.value = this.zOffset;
            material.uniforms.baseZ.value = baseZ;
        }
    }

    private splitFor(layerId: string): number {
        const { split, compare } = this.app.state;
        if (split === -1 || layerId === BASE_ID) return 0;
        return compare.includes(layerId) ? split : -split;
    }

    updateMaterials() {
        for (const [id, material] of this.layerMaterials) material.uniforms.split.value = this.splitFor(id);
        this.viewer.update();
    }

    // Scene-space camera height over the terrain surface under it, negative below ground;
    // null if no height data is loaded there.
    heightAboveGround(camera: THREE.Camera): number | null {
        const { x, y, z } = camera.position;
        const elevation = this.elevationAt(x, y);
        return elevation === null ? null : z - this.worldZ(elevation);
    }

    // Elevation (m) at a scene-world XY, or null if no height data is loaded there.
    elevationAt(worldX: number, worldY: number): number | null {
        const offset = this.app.state.offset;
        const [wmX, wmY] = this.app.toWm(worldX + offset.x, worldY + offset.y);
        const elevation = dtm.sampleElevation(wmX, wmY);
        return elevation !== null && Number.isFinite(elevation) ? elevation : null;
    }

    // March a ray against the loaded heightfield and return the scene-space
    // surface point under it, or null on a miss. The mesh is GPU-displaced, so
    // the CPU height data is sampled here instead.
    raycast(ray: THREE.Ray): THREE.Vector3 | null {
        const d = ray.direction;
        if (d.z >= -1e-4) return null; // not looking down at the ground

        const o = ray.origin;
        const maxT = (this.worldZ(MIN_ELEVATION_M) - o.z) / d.z; // ray exits below lowest terrain
        if (maxT <= 0) return null;

        const surfZ = (t: number): number => {
            const m = this.elevationAt(o.x + d.x * t, o.y + d.y * t);
            return m === null ? -Infinity : this.worldZ(m);
        };

        const STEPS = 512;
        let prevT = 0;
        for (let i = 1; i <= STEPS; i++) {
            const t = (i / STEPS) * maxT;
            if (o.z + d.z * t <= surfZ(t)) {
                let lo = prevT;
                let hi = t;
                for (let k = 0; k < 24; k++) {
                    const mid = 0.5 * (lo + hi);
                    if (o.z + d.z * mid > surfZ(mid)) lo = mid;
                    else hi = mid;
                }
                return o.clone().addScaledVector(d, hi);
            }
            prevT = t;
        }
        return null;
    }
}
